import React from "react"
import nl2br from "../utils/nl2br"
import { imageItemTransformer } from "../utils/transformers"
import withSuspense from "../utils/withSuspense"
import BodyStyles from "./BodyStyles"
import BaseContainer from "./Container"
import Image from "./ImageOptim"
import RichText from "./RichText"
import { InlineTitle } from "./Typography"
import { cva, cx } from "class-variance-authority"

const Video = withSuspense(React.lazy(() => import("./Video")))

export type BillboardRatio = "56.25vw" | "28.125vw"
export type BillboardDivider = "border" | "fade"

/* max height: header - banner padding - extra to show some text */
const billboardClasses = cva(
  "relative flex flex-col overflow-hidden items-center h-[100vw] md:min-h-[480px] md:max-h-[calc(100vh-147px-60px-24px)]",
  {
    variants: {
      divider: {
        fade: "after:block after:absolute after:content-[''] after:w-full after:h-full after:left-0 after:bottom-0 after:[background-image:linear-gradient(180deg,rgba(255,255,255,0)_55.9%,rgba(255,255,255,1)_100%)]",
        border: "",
      },
      preferredRatio: {
        "56.25vw": "md:h-[56.25vw]",
        "28.125vw": "md:h-[28.125vw]",
      },
    },
    defaultVariants: {
      divider: "border",
      preferredRatio: "56.25vw",
    },
  }
)

export interface BillboardProps {
  /**
   * Any additional children to append to the component
   * @deprecated Modular content should be added in Kontent and rendered using
   * `text`/`textLinkedItems`
   */
  appendChildren?: React.ReactNode
  /**
   * Container width for the text. When text is left-aligned the container should
   * match the page container width.
   */
  containerWidth?: "sm" | "md" | "lg" | "default"
  /**
   * Determines the color of the banner divider and eyebrow border when added
   */
  colorScheme?: "light" | "dark"
  /**
   * Determines the style of the banner divider
   */
  divider?: BillboardDivider
  /**
   * Optional text to display above the headline
   */
  eyebrow?: string
  /**
   * The headline text
   */
  headline?: string
  /**
   * The size of the headline text
   */
  headlineSize: "base" | "xl"
  /**
   * The image or video to display as the background
   */
  media: any
  /**
   * The type of media to display
   */
  mediaType: "image_element" | "video"
  /**
   * The ratio of the banner height to width
   */
  preferredRatio: BillboardRatio
  /**
   * The text to display in the banner
   */
  text?: string
  /**
   * The alignment of the text within the banner
   */
  textAlignment?: "left" | "center"
  /**
   * Data from Kontent to render components within the text
   */
  textLinkedItems?: any[]
  /**
   * Link data from Kontent to resolve links within the text
   */
  textLinks?: any[]
  /**
   * The position of the text within the banner
   * @deprecated Use `textAlignment` and `textVerticalPosition` instead
   */
  textPosition: "left" | "center" | "bottom"
  /**
   * The vertical position of the text within the banner
   */
  textVerticalPosition: "top" | "middle" | "bottom"
  /**
   * The width of the text container within the banner. This allows text below
   * the headline to be narrower than the headline itself. Useful for balancing
   * text and avoiding widows
   */
  textWidth?: "4/5" | "3/4" | "2/3" | "3/5" | "1/2"
}

// `export` had to be added to `const Billboard` to make props appear as controls in Storybook

/**
 * Billboard component for displaying a large image or video with text overlay
 */
export const Billboard = ({
  appendChildren,
  containerWidth,
  colorScheme,
  divider,
  eyebrow,
  headline,
  headlineSize,
  media,
  mediaType,
  preferredRatio,
  text,
  textAlignment,
  textLinkedItems,
  textLinks,
  // textPosition is replaced in the model by textAlignment and textVerticalPosition,
  // and can be removed as soon as this work is deployed
  textPosition,
  textVerticalPosition,
  textWidth,
}: BillboardProps) => {
  return (
    <div
      className={cx(billboardClasses({ divider, preferredRatio }), {
        "justify-start pt-12": textVerticalPosition === "top",
        // textPosition conditionals can be removed after deploy
        "justify-center":
          textVerticalPosition === "middle" || textPosition === "center",
        "justify-end":
          textVerticalPosition === "bottom" || textPosition === "bottom",
        "border-b-8": divider === "border",
        "border-orange": colorScheme === "dark",
        "border-cyan": colorScheme !== "dark",
      })}
    >
      {mediaType === `video` && (
        <Video
          video={media}
          cover={true}
          coverImageCropWidth={1600}
          className={`absolute inset-0 h-full`}
          ratio={0}
          suppressCaption={true}
          autoplay={true}
          muted={true}
          playsinline={true}
          loop
        />
      )}
      {mediaType === `image_element` && (
        <div className="absolute inset-0 h-full w-full">
          <Image
            {...imageItemTransformer(media)}
            cover={true}
            cropRatio={0.5625}
            cropWidth={1600}
          />
        </div>
      )}
      {/* textPosition can be replaced with textAlignment after deploy */}
      {/* Only make the container narrower if the banner text is left aligned
       * so it lines up w/ the page container
       */}
      <BaseContainer
        className="relative z-10"
        width={
          textPosition === `left` || textAlignment === `left`
            ? containerWidth
            : "default"
        }
      >
        <div
          className={cx(`w-full`, {
            "text-center mx-auto":
              textPosition === `center` || textAlignment === `center`,
            "text-left": textPosition === `left` || textAlignment === `left`,
            "pb-12":
              textPosition === `bottom` || textVerticalPosition === `bottom`,
            "md:w-4/5": textWidth === `4/5`,
            "md:w-3/4": textWidth === `3/4`,
            "md:w-2/3": textWidth === `2/3`,
            "md:w-3/5": textWidth === `3/5`,
            "md:w-3/5 xl:w-1/2": textWidth === `1/2`,
          })}
        >
          {eyebrow && (
            <InlineTitle
              colorScheme={colorScheme}
              contrast={true}
              border={textPosition !== `center` && textAlignment !== `center`}
              textAlign={
                textPosition === `center` || textAlignment === `center`
                  ? `center`
                  : undefined
              }
            >
              {eyebrow}
            </InlineTitle>
          )}
          {headline && (
            <h1
              className={`font-bold text-white ${
                eyebrow ? `mt-2 mb-0` : `my-0`
              } ${
                headlineSize === `xl`
                  ? `!leading-none text-3xl md:text-5xl lg:text-[4rem]`
                  : `leading-none text-3xl`
              }`}
            >
              {nl2br(headline)}
            </h1>
          )}
          {text && (
            <div className={`mt-4`}>
              {/* TODO: Add option to control text size, should default to _base_ */}
              <BodyStyles
                colorScheme={colorScheme}
                contrast={true}
                className={`text-base !leading-snug md:text-xl`}
              >
                <RichText
                  colorScheme={colorScheme}
                  content={text}
                  linkedItems={textLinkedItems}
                  links={textLinks}
                  contrast={true}
                />
              </BodyStyles>
            </div>
          )}
          {appendChildren}
        </div>
      </BaseContainer>
    </div>
  )
}

export default Billboard
